import React, { useState, useEffect, useRef } from 'react';
import type { SyntheticEvent } from 'react';
import { useSeasonYear, useSeasonYearContext } from '../context/SeasonYearContext';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Button,
  Menu,
  MenuItem,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { API_URL } from '../config';

import { authedFetch } from '../utils/apiUtils';
interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

interface FeaturePoint {
  position: number;
  points: number;
}

interface DivisionRequirement {
  requirement_id: number;
  division_name: string;
  min_races: number;
  year: number;
}

interface DateRule {
  rule_id: number;
  rule_name: string;
  start_date: string;
  end_date: string;
  description: string;
}

interface BonusRule {
  rule_id: number;
  min_cars: number;
  bonus_points: number;
  max_bonus: number;
  year: number;
}

interface AlertState {
  show: boolean;
  message: string;
  severity: 'info' | 'success' | 'warning' | 'error';
}

function TabPanel({ children, value, index }: TabPanelProps): React.ReactElement {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`points-tabpanel-${index}`}
      aria-labelledby={`points-tab-${index}`}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '—';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const yearOptionsAround = (year: number): number[] => {
  const years: number[] = [];
  for (let i = 1; i >= -3; i -= 1) years.push(year + i);
  return Array.from(new Set(years)).sort((a, b) => b - a);
};

function PointsSchema(): React.ReactElement {
  const seasonYear = useSeasonYear();
  const { setSeasonYear: setGlobalSeasonYear } = useSeasonYearContext();
  const [tabValue, setTabValue] = useState(0);
  const [featurePoints, setFeaturePoints] = useState<FeaturePoint[]>([]);
  const [divisionRequirements, setDivisionRequirements] = useState<DivisionRequirement[]>([]);
  const [dateRules, setDateRules] = useState<DateRule[]>([]);
  const [bonusRules, setBonusRules] = useState<BonusRule[]>([]);
  const [alert, setAlert] = useState<AlertState>({ show: false, message: '', severity: 'info' });
  const [loading, setLoading] = useState(true);
  const [seasonAnchorEl, setSeasonAnchorEl] = useState<null | HTMLElement>(null);
  const fetchSeqId = useRef(0);

  useEffect(() => {
    const seqId = ++fetchSeqId.current;
    setLoading(true);
    (async () => {
      try {
        const [featureRes, divReqRes, dateRulesRes, bonusRulesRes] = await Promise.all([
          authedFetch(`${API_URL}/points/finish-positions?is_feature=true`),
          authedFetch(`${API_URL}/points/division-requirements?year=${seasonYear}`),
          authedFetch(`${API_URL}/points/date-rules?year=${seasonYear}`),
          authedFetch(`${API_URL}/points/bonus-rules?year=${seasonYear}`),
        ]);
        if (seqId !== fetchSeqId.current) return;

        if (!featureRes.ok) throw new Error('Failed to fetch feature points');
        if (!divReqRes.ok) throw new Error('Failed to fetch division requirements');
        if (!dateRulesRes.ok) throw new Error('Failed to fetch date rules');
        if (!bonusRulesRes.ok) throw new Error('Failed to fetch bonus rules');

        const [featureData, divReqData, dateRulesData, bonusRulesData] = await Promise.all([
          featureRes.json(),
          divReqRes.json(),
          dateRulesRes.json(),
          bonusRulesRes.json(),
        ]);
        if (seqId !== fetchSeqId.current) return;

        setFeaturePoints(Array.isArray(featureData) ? featureData : []);
        setDivisionRequirements(Array.isArray(divReqData) ? divReqData : []);
        setDateRules(Array.isArray(dateRulesData) ? dateRulesData : []);
        setBonusRules(Array.isArray(bonusRulesData) ? bonusRulesData : []);
        setAlert({ show: false, message: '', severity: 'info' });
      } catch (err) {
        if (seqId !== fetchSeqId.current) return;
        console.error('Error fetching points data:', err);
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setAlert({
          show: true,
          message: `Error loading points data: ${errorMessage}`,
          severity: 'error',
        });
      } finally {
        if (seqId === fetchSeqId.current) {
          setLoading(false);
        }
      }
    })();
  }, [seasonYear]);

  const handleTabChange = (_event: SyntheticEvent, newValue: number): void => {
    setTabValue(newValue);
  };

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" component="h1">
            Points Schemas
          </Typography>
          <Button
            onClick={(e) => setSeasonAnchorEl(e.currentTarget)}
            endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 9999,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              color: 'text.primary',
              fontWeight: 500,
              fontSize: '0.8125rem',
              py: 0.25,
              px: 1.5,
              minHeight: 28,
              '&:hover': { backgroundColor: 'action.hover', borderColor: 'text.disabled' },
            }}
          >
            <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.75, fontWeight: 500 }}>
              Season
            </Typography>
            <Typography component="span" variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {seasonYear}
            </Typography>
          </Button>
          <Menu
            anchorEl={seasonAnchorEl}
            open={Boolean(seasonAnchorEl)}
            onClose={() => setSeasonAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: { sx: { mt: 0.5, minWidth: 100 } } }}
          >
            {yearOptionsAround(seasonYear).map((y) => (
              <MenuItem
                key={y}
                selected={y === seasonYear}
                onClick={() => {
                  setGlobalSeasonYear(y);
                  setSeasonAnchorEl(null);
                }}
              >
                {y}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>

      {alert.show && (
        <Alert
          severity={alert.severity}
          sx={{ mb: 2 }}
          onClose={() => setAlert({ ...alert, show: false })}
        >
          {alert.message}
        </Alert>
      )}

      <Paper sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              minHeight: 44,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                minHeight: 44,
                fontSize: '0.875rem',
              },
            }}
          >
            <Tab label="Feature Points" id="points-tab-0" aria-controls="points-tabpanel-0" />
            <Tab label="Division Requirements" id="points-tab-1" aria-controls="points-tabpanel-1" />
            <Tab label="Season Rules" id="points-tab-2" aria-controls="points-tabpanel-2" />
            <Tab label="Bonus Points" id="points-tab-3" aria-controls="points-tabpanel-3" />
          </Tabs>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ p: 3 }}>
            <TabPanel value={tabValue} index={0}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 120 }}>Position</TableCell>
                      <TableCell>Points</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {featurePoints.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} sx={{ color: 'text.disabled', textAlign: 'center', py: 3 }}>
                          No feature points configured
                        </TableCell>
                      </TableRow>
                    ) : (
                      featurePoints.map((row) => (
                        <TableRow key={row.position}>
                          <TableCell sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
                            {row.position}
                          </TableCell>
                          <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{row.points}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Points drop by 1 for positions 26 to 40 when more than 25 starters are approved.
              </Typography>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Division</TableCell>
                      <TableCell sx={{ width: 160 }}>Minimum Races</TableCell>
                      <TableCell sx={{ width: 100 }}>Year</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {divisionRequirements.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} sx={{ color: 'text.disabled', textAlign: 'center', py: 3 }}>
                          No division requirements for {seasonYear}
                        </TableCell>
                      </TableRow>
                    ) : (
                      divisionRequirements.map((row) => (
                        <TableRow key={row.requirement_id}>
                          <TableCell sx={{ fontWeight: 500 }}>{row.division_name}</TableCell>
                          <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{row.min_races}</TableCell>
                          <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{row.year}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Rule</TableCell>
                      <TableCell sx={{ width: 140 }}>Start Date</TableCell>
                      <TableCell sx={{ width: 140 }}>End Date</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dateRules.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ color: 'text.disabled', textAlign: 'center', py: 3 }}>
                          No season rules for {seasonYear}
                        </TableCell>
                      </TableRow>
                    ) : (
                      dateRules.map((rule) => (
                        <TableRow key={rule.rule_id}>
                          <TableCell sx={{ fontWeight: 500 }}>{rule.rule_name}</TableCell>
                          <TableCell>{formatDate(rule.start_date)}</TableCell>
                          <TableCell>{formatDate(rule.end_date)}</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>{rule.description}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 140 }}>Minimum Cars</TableCell>
                      <TableCell sx={{ width: 200 }}>Points per Extra Car</TableCell>
                      <TableCell sx={{ width: 160 }}>Maximum Bonus</TableCell>
                      <TableCell sx={{ width: 100 }}>Year</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bonusRules.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ color: 'text.disabled', textAlign: 'center', py: 3 }}>
                          No bonus rules for {seasonYear}
                        </TableCell>
                      </TableRow>
                    ) : (
                      bonusRules.map((rule) => (
                        <TableRow key={rule.rule_id}>
                          <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{rule.min_cars}</TableCell>
                          <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{rule.bonus_points}</TableCell>
                          <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{rule.max_bonus}</TableCell>
                          <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{rule.year}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                One bonus point per car over 25 entered, capped at 5 points.
              </Typography>
            </TabPanel>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default PointsSchema;
